"use client";
import { useAtomValue } from "jotai";
import dynamic from "next/dynamic";
import { useState } from "react";
import { CrossSellViewMode } from "@/api/AppApi/services/company/interfaces/ICompany";
import { cartItemSavedCountAtom } from "@/hooks/cartAtoms";
import { useCartCrossSell } from "./useCartCrossSell";
import { useCartCrossSellIds } from "./useCartCrossSellIds";

// Never part of a first paint, and only a minority of stores use popup mode.
const CartCrossSellPopup = dynamic(() => import("./CartCrossSellPopup"), {
  ssr: false,
});

interface Props {
  show: boolean;
  confirmsAdd: boolean;
  onClose: () => void;
}

/**
 * One add-to-cart moment. Mounted fresh per opening (see the `key` in
 * `CartCrossSellPopupHost`), so nothing carries over between adds.
 *
 * Nothing appears until `POST /carts/items` has actually come back successful.
 * The add-to-cart buttons raise `goToCartAtom` on the click — before the request
 * is even sent, and the product card debounces it by a further 500ms — so
 * opening on that would announce an add that may yet fail, and we would not know
 * which product to recommend against.
 */
const CrossSellAddMoment: React.FC<Props> = ({ show, confirmsAdd, onClose }) => {
  const savedCount = useAtomValue(cartItemSavedCountAtom);
  // Captured at mount, before this add can have landed.
  const [countAtOpen] = useState(savedCount);
  const addSucceeded = savedCount > countAtOpen;

  // Safe to read the moment `addSucceeded` flips: the mutation updates the cart
  // cache before it records the write, so these ids already include the product
  // just added. Gating the query on it also stops the PREVIOUS basket being
  // queried — add A, dismiss, add B, and that request is already cached, so A's
  // recommendations would paint instantly under a heading about B.
  const crossSell = useCartCrossSell(
    useCartCrossSellIds(),
    CrossSellViewMode.popup,
    show && addSucceeded,
  );

  return (
    <CartCrossSellPopup
      reason="added"
      show={show && addSucceeded}
      onHide={onClose}
      crossSell={crossSell}
      confirmsAdd={confirmsAdd}
      isResolving={crossSell.isLoading}
    />
  );
};

export default CrossSellAddMoment;
